'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Package, Calendar, ArrowRight, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { purchaseReceipts, suppliers, purchaseOrders } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import { purchaseReceiptStatusConfig, type PurchaseReceiptStatus } from '../types';
import type { PurchaseReceipt } from '../types';

// 입고 등록 모달
function PurchaseReceiptAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', quantity: 1, unitPrice: 0 }]);

  const addItem = () => setItems([...items, { itemName: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (index: number) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="입고 등록"
      description="새로운 입고 내역을 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>입고 등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="입고번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="발주서 참조">
            <FormSelect>
              <option value="">선택 안함</option>
              {purchaseOrders.filter(po => po.status === 'submitted' || po.status === 'to_receive').map(po => (
                <option key={po.id} value={po.id}>{po.code} - {po.supplierName}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="공급업체" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {suppliers.filter(s => s.isActive).map((supplier) => (
                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="입고일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="입고 창고" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="WH001">본사 창고</option>
              <option value="WH002">부품 창고</option>
              <option value="WH003">포장재 창고</option>
            </FormSelect>
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">입고 품목</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목</th>
                  <th className="text-right p-3 font-medium w-24">수량</th>
                  <th className="text-right p-3 font-medium w-32">단가</th>
                  <th className="text-right p-3 font-medium w-32">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">품목 선택</option>
                        <option value="mat-001">원지 (A등급)</option>
                        <option value="mat-002">원지 (B등급)</option>
                        <option value="mat-003">골심지</option>
                        <option value="mat-004">접착제</option>
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={1} className="text-right" /></td>
                    <td className="p-2"><FormInput type="number" min={0} defaultValue={0} className="text-right" /></td>
                    <td className="p-2 text-right font-medium">₩0</td>
                    <td className="p-2">
                      <button onClick={() => removeItem(index)} className="p-1 rounded hover:bg-accent" disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="입고 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function PurchaseReceiptListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return purchaseReceipts.filter((receipt) => {
      // 검색 필터
      const matchesSearch =
        receipt.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        receipt.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (receipt.purchaseOrderCode && receipt.purchaseOrderCode.toLowerCase().includes(searchQuery.toLowerCase()));

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || receipt.status === selectedStatus;

      // 공급업체 필터
      const matchesSupplier =
        selectedSupplier === 'all' || receipt.supplierId === selectedSupplier;

      return matchesSearch && matchesStatus && matchesSupplier;
    });
  }, [searchQuery, selectedStatus, selectedSupplier]);

  const columns = [
    {
      key: 'code',
      label: '입고번호',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary">{String(value)}</span>
      ),
    },
    {
      key: 'supplierName',
      label: '공급업체',
      width: '18%',
      sortable: true,
      render: (value: unknown, row: PurchaseReceipt) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
            <Package className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.purchaseOrderCode && (
              <div className="text-xs text-muted-foreground truncate">
                발주: {row.purchaseOrderCode}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'receiptDate',
      label: '입고일',
      width: '11%',
      sortable: true,
      render: (value: unknown) => (
        <div className="flex items-center gap-1 text-sm">
          <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className="whitespace-nowrap">{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'warehouse',
      label: '창고',
      width: '10%',
      render: (value: unknown) => {
        const warehouseNames: Record<string, string> = {
          WH001: '본사 창고',
          WH002: '부품 창고',
          WH003: '포장재 창고',
        };
        return <span className="text-sm whitespace-nowrap">{warehouseNames[String(value)] || String(value)}</span>;
      },
    },
    {
      key: 'totalQty',
      label: '수량',
      width: '8%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'totalAmount',
      label: '금액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as PurchaseReceiptStatus;
        const config = purchaseReceiptStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    purchaseReceipts.forEach((receipt) => {
      stats[receipt.status] = (stats[receipt.status] || 0) + 1;
    });
    return stats;
  }, []);

  // 요약 정보
  const totalReceipts = purchaseReceipts.length;
  const totalQty = purchaseReceipts.reduce((sum, r) => sum + r.totalQty, 0);
  const totalAmount = purchaseReceipts.reduce((sum, r) => sum + r.totalAmount, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="입고 관리"
        description="구매 입고 내역을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            입고 등록
          </button>
        }
      />

      {/* 상태별 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(purchaseReceiptStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">
              {statusStats[status] || 0}건
            </div>
          </button>
        ))}
      </div>

      {/* 요약 정보 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 입고</div>
          <div className="text-2xl font-bold mt-1">{totalReceipts}건</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">입고 수량</div>
          <div className="text-2xl font-bold mt-1">{totalQty.toLocaleString()}</div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">입고 금액</div>
          <div className="text-2xl font-bold mt-1">{formatCurrency(totalAmount)}</div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="입고번호, 발주번호, 공급업체 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(purchaseReceiptStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        {/* 공급업체 필터 */}
        <select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 공급업체</option>
          {suppliers
            .filter((s) => s.isActive)
            .map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 입고 등록 모달 */}
      <PurchaseReceiptAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
